/**
 * EVResultsPreview - Compact results preview for expandable scenario rows
 *
 * Shows key metrics at a glance when a scenario row is expanded in the
 * UnifiedScenarioTable. Displays peak demand, annual energy/cost, and
 * strategy comparison.
 */

import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import {
    BoltOutlined as PeakIcon,
    AttachMoney as CostIcon,
    ElectricBolt as EnergyIcon,
} from '@mui/icons-material';
import { useThemeMode, vortxColors, vortxTypography } from '@vortx/shared-ui';

function formatCurrency(value) {
    if (value === null || value === undefined) return '\u2014';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

function formatPower(value) {
    if (value === null || value === undefined) return '\u2014';
    return `${Math.round(value).toLocaleString()} kW`;
}

function formatEnergy(value) {
    if (value === null || value === undefined) return '\u2014';
    return `${Math.round(value).toLocaleString()} kWh`;
}

function MetricCard({ icon, label, value, isRetro }) {
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: isRetro ? 0 : 1,
            bgcolor: isRetro ? 'rgba(0, 0, 0, 0.3)' : alpha(vortxColors.warmStone, 0.15),
            border: isRetro
                ? '1px solid rgba(95, 212, 196, 0.2)'
                : `1px solid ${alpha(vortxColors.teal, 0.15)}`,
            minWidth: 140,
        }}>
            {React.cloneElement(icon, {
                sx: {
                    fontSize: 16,
                    color: isRetro ? '#5fd4c4' : vortxColors.teal,
                }
            })}
            <Box>
                <Typography sx={{
                    fontSize: '0.6rem',
                    color: isRetro ? '#888' : 'text.secondary',
                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                    textTransform: 'uppercase',
                    letterSpacing: isRetro ? '0.05em' : 'normal',
                }}>
                    {label}
                </Typography>
                <Typography sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: isRetro ? '#5fd4c4' : vortxColors.charcoal,
                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

export default function EVResultsPreview({ scenario }) {
    const { isRetro } = useThemeMode();
    const results = scenario?.results;

    if (!results || Object.keys(results).length === 0) {
        return (
            <Typography sx={{
                color: isRetro ? '#888' : 'text.secondary',
                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                fontSize: '0.75rem',
                py: 1,
            }}>
                {isRetro ? '> no results available' : 'No results available. Run a simulation first.'}
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 0.5 }}>
            <MetricCard
                icon={<PeakIcon />}
                label="Peak Demand"
                value={formatPower(results.peak_demand_kw)}
                isRetro={isRetro}
            />
            <MetricCard
                icon={<EnergyIcon />}
                label="Annual Energy"
                value={formatEnergy(results.annual_energy_kwh)}
                isRetro={isRetro}
            />
            <MetricCard
                icon={<CostIcon />}
                label="Annual Cost"
                value={formatCurrency(results.annual_cost)}
                isRetro={isRetro}
            />
            {results.strategies && Object.keys(results.strategies).length > 0 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                }}>
                    <Typography sx={{
                        fontSize: '0.6rem',
                        color: isRetro ? '#888' : 'text.secondary',
                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                    }}>
                        {Object.keys(results.strategies).length} strategies simulated
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
